import { handleLogout } from 'src/helpers/account_helpers';
import { clearEnergyHeatmapCache } from 'src/components/EnergyMonitoring/helpers/energy_heatmap_cache';
import { resetEnergyMonitoringStats } from 'src/components/EnergyMonitoring/helpers/store';
import CacheStatisticRequest from 'src/helpers/statistics/request/chart_cache_statistics_helpers';
import { user_data } from 'src/stores/account/user';
import { app, os } from 'src/stores/session/native-bridge';
import { isRetrying, RetryStatus } from 'src/stores/session/failed-requests';
import { get } from 'svelte/store';
import { setUpI18n } from './i18n';
import { shouldForceLogout, showForceLogoutAlert } from './auth_errors';
import { getRemainingTime } from './auth_token';
import type { UserData } from 'src/types/UserData';
import { get_user_pin, handleToken, handle_base_url, pullDataWithLoading } from './http';
import { loginRequest, refreshTokenRequest } from './iotu/v2_users_auth';
import { socket_disconnect } from './socket';
import type {
	LoginErrorResponse,
	LoginResult,
	MultiUnitLoginResult,
} from 'src/types/responses/usersAuthResponse';
import { resetDrawer } from 'src/helpers/drawer_helpers';
import { NativeOperationSystem } from './init/types/init-type';
import { login_mobile_android } from './native_container_requests_android';
import { credentials_put_ios, login_mobile_ios } from './native_container_requests_ios';
import { currentUnitId, homes, refreshHomes } from 'src/stores/account/home';
import { notifyNativeHomeSwitched } from 'src/helpers/native_bridge_helpers';
import { loadingData } from 'src/stores/ui/loading-data';
import { getCachedUnitSession, setCachedUnitSession } from './unit_token_cache';

/**
 * Login (v2)
 * User login with email and password
 * - Replacement of the legacy v1 standard login
 *
 * @param {string} email - User's email
 * @param {string} pass - User's hashed password
 * @param {string} [unitId] - Optional unit ID for multi-unit accounts
 * @param {string} [subId] - Optional sub-unit ID for shared homes
 * @returns {Promise<LoginResult>} - Returns user data, multi-unit selection prompt, or error response
 *
 */
export const authLogin = async (
	email: string,
	pass: string,
	unitId?: string,
	subId?: string,
): Promise<LoginResult> => {
	try {
		const result = await loginRequest(email, pass, unitId, subId);
		const defer = needsUnitSelection(result, unitId);

		// Skip on deferred unit selection: the session is committed later, once the
		// picker resolves a home, so the active account isn't disturbed (FE-2159).
		if (!defer && result.data?.token) {
			await handleInitialUserdata(result.data, unitId, subId);
		}

		resetDrawer('login');

		return result;
	} catch (error: LoginErrorResponse | any) {
		console.log('[AUTHv2] Standard login: ', error);
		return error?.response?.data || error;
	} finally {
		isRetrying.update(RetryStatus.INVALID);
	}
};

/**
 * Thrown by {@link switchUnit} when the requested home can no longer be
 * reached — it was deleted, unshared, or transferred away, so the backend
 * responds with the remaining units instead of a session token. Callers use
 * this to show a precise message and drop the stale home from the picker
 * (FE-2022).
 */
export class HomeUnavailableError extends Error {
	constructor(
		public readonly unitId?: string,
		public readonly subId?: string,
	) {
		super('Requested home is no longer available');
		this.name = 'HomeUnavailableError';
	}
}

export const isMultiUnitResult = (result: LoginResult): result is MultiUnitLoginResult =>
	('multipleUnits' in result && result.multipleUnits === true) ||
	('availableUnits' in result && result.availableUnits !== undefined) ||
	('availableSubUnits' in result && result.availableSubUnits !== undefined);

/**
 * Returns true when the login result requires the user to pick a home and no
 * unit has been requested yet — i.e. persisting session data should be
 * deferred until the picker resolves.
 */
const needsUnitSelection = (result: LoginResult, requestedUnitId?: string): boolean => {
	if (requestedUnitId) return false;
	if (!isMultiUnitResult(result)) return false;
	const owned = Object.keys(result.availableUnits ?? {}).length;
	const shared = Object.keys(result.availableSubUnits ?? {}).length;
	return owned + shared > 1;
};

/**
 * Token refresh (v2)
 * Refreshes the access token using the refresh token
 * Migration: If the refresh token is missing or expired, it falls back to the login request
 *
 * @returns {Promise<{token: string}>} - Returns the new access token
 */
export const authRefreshToken = async (): Promise<{ token: string } | void> => {
	if (get(isRetrying) === RetryStatus.IN_PROGRESS) {
		return;
	}

	isRetrying.update(RetryStatus.IN_PROGRESS);

	let lastRefreshToken = user_data.getRefreshToken();

	if (!lastRefreshToken || getRemainingTime(lastRefreshToken) <= 0) {
		console.warn('[AUTHv2] No refresh token found, fallback to login');
		return fallbackToLogin();
	}

	try {
		const result = await refreshTokenRequest(lastRefreshToken);
		console.log('[AUTHv2] Refresh Token Data:', result);

		handleUpdatedUserdata(result.data);

		isRetrying.update(RetryStatus.INVALID);

		return { token: result.data.token };
	} catch (error: LoginErrorResponse | any) {
		console.log('[AUTHv2] Refresh token error response', error);

		return handleRefreshTokenError(error?.response?.data);
	}
};

/**
 * Handle initial user data after login
 * - Sets the user data in the store
 * - Sets the base URL for the user API instance
 * - Sets the token for the API
 * - Sets up i18n
 *
 * @param {UserData} data - The user data to set
 * @param {string} [unitId] - Owner `unit_id` requested in the login call (when
 *   selected from a multi-unit / sub-unit picker). Used as the source of truth
 *   for `currentUnitId` since the JWT's `user_id` is the sub-user's account id
 *   in a sub session, not the owner unit_id that indexes `available_*_units`.
 * @param {string} [subId] - Sub-unit id requested in the login call (shared home).
 *   Forwarded for completeness so this function has the full sub-session context.
 */
const handleInitialUserdata = async (data: Partial<UserData>, unitId?: string, subId?: string) => {
	if (!data || !data.user_api_url || !data.token || !data.lang) {
		console.error('[AUTHv2] Invalid user data', data);
		return;
	}

	// Clear stale homes before the new session commits; `refreshHomes` repopulates.
	// In-app list only — the imminent refresh updates natives without a flicker (FE-2197).
	homes.set({ list: [], fetchStatus: 'loading' });

	user_data.set(data as UserData);
	currentUnitId.set(String(unitId ?? data.user_id ?? ''));
	handle_base_url(data.user_api_url);
	handleToken(data.token);
	await setUpI18n(data.lang);

	// Seed the per-unit cache so switching away and back to this first home
	// reuses its token instead of issuing another login. Keyed to match the
	// `switchUnit(unitId, subId)` callers.
	if (unitId) setCachedUnitSession(data.email, unitId, subId, data);
};

/**
 * Handle updated user data from refresh token
 * @param data
 */
const handleUpdatedUserdata = async (data: Partial<UserData>) => {
	user_data.update(data);
	handle_base_url(data.user_api_url);
	handleToken(data.token);
	await setUpI18n(data.lang);
};

/**
 * Handle Error from Token refresh request
 * @param responseData - The response data from the refresh token request
 * @returns {Promise<void>}
 */
const handleRefreshTokenError = async (responseData: {
	isok: boolean;
	error?: string;
}): Promise<void> => {
	if (shouldForceLogout(responseData)) {
		console.error('[AUTHv2] Refresh token request FAILED: ', responseData);

		await forceLogout();
		return;
	}

	isRetrying.update(RetryStatus.INVALID);
};

/**
 * Force logout
 * Logout the user and show error toast.
 * Always clears local/native session and lands on `/login` (no profile auto-switch),
 * so invalidated sessions are not left partially authenticated on web or native.
 *
 * @param showAlert optional alert before logout (defaults to session expired)
 */
export const forceLogout = async (showAlert: () => void = showForceLogoutAlert) => {
	console.warn('[AUTHv2] Force logout');

	isRetrying.update(RetryStatus.SKIP);

	showAlert();
	return await handleLogout(undefined, false, { forceToLogin: true });
};

/**
 * Fallback to login request for token refresh
 *   Backwards compatibility for users without refresh token
 * - Migration: users who do not have a refresh token
 * - Expired refresh token: We still preserve the password hash, so relogin is possible
 * * @todo: Remove when all users have refresh token / we deprecate password hashes
 * @returns {Promise<{token: string}>} - Returns the new access token
 */
const fallbackToLogin = async () => {
	try {
		const old_data: UserData = get(user_data);
		const result = await loginRequest(old_data.email, old_data.password);

		await handleUpdatedUserdata(result.data);
		currentUnitId.set(String(result.data?.user_id ?? ''));

		isRetrying.update(RetryStatus.INVALID);
		return { token: result.data.token };
	} catch (error: LoginErrorResponse | any) {
		console.error('[AUTHv2] Fallback login error', error);

		return handleRefreshTokenError(error?.response?.data);
	}
};

/**
 * Resolve the session data for a unit switch, preferring a cached session so a
 * return visit to a home already opened this session avoids a `/login` round-trip.
 *
 * Order of preference:
 * 1. Valid cached access token -> reuse it, no network.
 * 2. Expired access token but valid cached refresh token -> refresh via that
 *    unit's refresh token (point the HTTP client at its instance first).
 * 3. Otherwise, or on ANY failure in the cache path -> full `loginRequest`.
 *
 * Every successful path caches its result. The cache can never regress the
 * switch: any problem falls through to a real login.
 *
 * @returns {Promise<Partial<UserData>>} the session data to apply
 * @throws when a full login is required but fails
 */
const resolveUnitSession = async (
	email: string,
	password: string,
	unitId?: string,
	subId?: string,
): Promise<Partial<UserData>> => {
	const cached = getCachedUnitSession(email, unitId, subId);

	if (cached?.token && getRemainingTime(cached.token) > 0) {
		return cached;
	}

	if (cached?.refresh_token && cached.user_api_url && getRemainingTime(cached.refresh_token) > 0) {
		try {
			// Refresh hits the unit's own instance, so target it before refreshing.
			handle_base_url(cached.user_api_url);
			const refreshed = await refreshTokenRequest(cached.refresh_token);
			if (refreshed?.isok && refreshed.data?.token) {
				setCachedUnitSession(email, unitId, subId, refreshed.data);
				return refreshed.data;
			}
		} catch (error) {
			console.warn('[AUTHv2] Cached unit refresh failed, falling back to login', error);
		}
	}

	const result = await loginRequest(email, password, unitId, subId);
	if (!(isMultiUnitResult(result) || (result?.isok && result.data?.token))) {
		throw new Error('Unit switch login failed');
	}
	setCachedUnitSession(email, unitId, subId, result.data);
	return result.data;
};

/**
 * Switch active unit
 * Re-authenticates with a specific unit ID (or sub-unit ID) to switch the active home.
 * Preserves `available_units` in the store since `user_data.update` is a merge operation.
 *
 * @param {string} [unitId] - The unit ID to switch to (owned home)
 * @param {string} [subId] - The sub-unit ID to switch to (shared home)
 * @returns {Promise<void>}
 */
export const switchUnit = async (unitId?: string, subId?: string): Promise<void> => {
	const { email, password } = get(user_data);
	const data = await resolveUnitSession(email, password, unitId, subId);

	resetDrawer('login');

	// A successful switch always carries a fresh token. When the target home has
	// been deleted / unshared / transferred, the backend instead returns only the
	// still-available units (no token) — surface that as a distinct, actionable
	// error rather than a generic failure so the caller can prune it (FE-2022).
	if (!data?.token) {
		throw new HomeUnavailableError(unitId, subId);
	}

	await handleUpdatedUserdata(data);
	currentUnitId.set(String(unitId ?? data?.user_id ?? ''));

	// Account email is unchanged across homes; drop stats caches keyed only by
	// URL/body (or email) so mounted energy widgets cannot reuse the previous
	// home's history. Loaders also depend on $currentUnitId to refetch.
	CacheStatisticRequest.clear();
	clearEnergyHeatmapCache();
	resetEnergyMonitoringStats();

	notifyNativeHomeSwitched(String(unitId ?? get(currentUnitId) ?? ''), subId);

	// When a load is already in progress pullDataWithLoading() is a no-op, so
	// refresh homes explicitly to keep the store and natives in sync.
	if (get(loadingData)) {
		await refreshHomes();
	}

	// Drop sockets bound to the previous home's server so socket_connect() in
	// pullDataWithLoading() opens fresh ones against the new notifications_urls;
	// otherwise cross-server home switches keep using a stale WS (FE-1794).
	socket_disconnect();

	await pullDataWithLoading();

	// The PIN is scoped to the active unit/session, but the unit-switch login
	// response does not carry it (`normalizeUsermetadata` only sets `lock_pin`
	// when present), so the store keeps the previous unit's value. Re-fetch it
	// explicitly like the login / account-switch flows do (FE-1949).
	get_user_pin();
};

export const nativeLogin = (
	email: string,
	pass: string,
	apiUrl: string,
	token: string,
	lang: string,
) => {
	const isApp = get(app);
	const operatingSystem = get(os);

	if (!isApp) {
		return;
	}

	if (operatingSystem === NativeOperationSystem.IOS) {
		login_mobile_ios(email.toLowerCase(), apiUrl, lang);
		credentials_put_ios(email.toLowerCase(), pass, token);
		return;
	}

	login_mobile_android(email.toLowerCase(), apiUrl, lang);
};
